import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Paper,
  Fade,
  Zoom,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Add, 
  CalendarToday, 
  List, 
  Schedule,
  CheckCircle,
  AccessTime,
  Event,
  Refresh,
  Download,
} from '@mui/icons-material';
import AppointmentCalendar from '../components/appointments/AppointmentCalendar';
import AppointmentForm from '../components/appointments/AppointmentForm';
import AppointmentDetail from '../components/appointments/AppointmentDetail';
import DataTable, { Column } from '../components/common/DataTable';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { format, addDays } from 'date-fns';
import { Appointment } from '../types/appointment';
import { appointmentService } from '../services/appointmentService';

const columns: Column[] = [
  {
    id: 'customerName',
    label: 'Customer',
    format: (value: string, row: Appointment) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: '#E8B4B8',
            color: 'white',
            width: 40,
            height: 40,
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          {value.charAt(0)}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.customerPhone}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    id: 'appointmentDate',
    label: 'Date & Time',
    format: (value: string, row: Appointment) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {format(new Date(value), 'MMM dd, yyyy')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.startTime} - {row.endTime}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'serviceName',
    label: 'Service',
    format: (value: string, row: Appointment) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          with {row.staffName}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'totalAmount',
    label: 'Amount',
    format: (value: number) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          ₹{value.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Total fee
        </Typography>
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => {
      const getStatusConfig = (status: string) => {
        switch (status) {
          case 'confirmed': 
            return { 
              color: 'success' as const, 
              bgcolor: 'rgba(72, 187, 120, 0.1)', 
              textColor: '#48BB78',
              label: 'Confirmed'
            };
          case 'in_progress': 
            return { 
              color: 'warning' as const, 
              bgcolor: 'rgba(237, 137, 54, 0.1)', 
              textColor: '#ED8936',
              label: 'In Progress'
            };
          case 'completed': 
            return { 
              color: 'info' as const, 
              bgcolor: 'rgba(66, 153, 225, 0.1)', 
              textColor: '#4299E1',
              label: 'Completed'
            };
          case 'cancelled': 
            return { 
              color: 'error' as const, 
              bgcolor: 'rgba(245, 101, 101, 0.1)', 
              textColor: '#f56565',
              label: 'Cancelled'
            };
          default: 
            return { 
              color: 'default' as const, 
              bgcolor: 'rgba(160, 174, 192, 0.1)', 
              textColor: '#A0AEC0',
              label: 'Scheduled'
            };
        }
      };
      
      const config = getStatusConfig(value);
      
      return (
        <Chip
          label={config.label}
          sx={{
            bgcolor: config.bgcolor,
            color: config.textColor,
            fontWeight: 600,
            border: `1px solid ${config.textColor}30`,
            textTransform: 'capitalize',
          }}
          size="small"
        />
      );
    },
  },
];

const Appointments: React.FC = () => {
  const dispatch = useAppDispatch();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [preselectedDate, setPreselectedDate] = useState<Date | undefined>();
  const [preselectedTime, setPreselectedTime] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchAppointments = async () => {
    setFetching(true);
    try {
      const dateFrom = new Date();
      dateFrom.setMonth(dateFrom.getMonth() - 1);
      const dateTo = new Date();
      dateTo.setMonth(dateTo.getMonth() + 2);
      const result = await appointmentService.getAppointments({
        dateFrom: dateFrom.toISOString().split('T')[0],
        dateTo: dateTo.toISOString().split('T')[0],
        page: 1,
        limit: 100,
      });
      setAppointments(result.appointments || []);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to load appointments',
      }));
      setAppointments([]);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setDetailOpen(true);
  };

  const handleAddAppointment = (date: Date, time?: string) => {
    setPreselectedDate(date);
    setPreselectedTime(time);
    setEditingAppointment(null);
    setFormOpen(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setPreselectedDate(undefined);
    setPreselectedTime(undefined);
    setFormOpen(true);
  };

  const handleFormSubmit = async (appointmentData: any) => {
    setLoading(true);
    try {
      const payload = {
        customerId: appointmentData.customerId,
        staffId: appointmentData.staffId,
        services: appointmentData.services || [],
        appointmentDate: appointmentData.appointmentDate,
        startTime: appointmentData.startTime,
        notes: appointmentData.notes || '',
      };
      if (editingAppointment) {
        const updated = await appointmentService.updateAppointment(editingAppointment.id, payload);
        setAppointments(appointments.map(apt => 
          apt.id === editingAppointment.id ? updated : apt
        ));
        dispatch(addNotification({ type: 'success', message: 'Appointment updated successfully' }));
      } else {
        const created = await appointmentService.createAppointment(payload);
        setAppointments([...appointments, created]);
        dispatch(addNotification({ type: 'success', message: 'Appointment booked successfully' }));
      }
      setFormOpen(false);
      setEditingAppointment(null);
      setPreselectedDate(undefined);
      setPreselectedTime(undefined);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save appointment',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId: string, newStatus: Appointment['status']) => {
    try {
      let updated: Appointment;
      if (newStatus === 'confirmed') {
        updated = await appointmentService.confirmAppointment(appointmentId);
      } else if (newStatus === 'completed') {
        updated = await appointmentService.completeAppointment(appointmentId);
      } else if (newStatus === 'cancelled') {
        updated = await appointmentService.cancelAppointment(appointmentId);
      } else {
        updated = await appointmentService.updateAppointmentStatus(appointmentId, newStatus);
      }
      setAppointments(appointments.map(apt => apt.id === appointmentId ? updated : apt));
      const statusMessages: Record<string, string> = {
        scheduled: 'Appointment scheduled',
        confirmed: 'Appointment confirmed',
        in_progress: 'Appointment started',
        completed: 'Appointment completed',
        cancelled: 'Appointment cancelled',
      };
      dispatch(addNotification({
        type: 'success',
        message: statusMessages[newStatus] || 'Appointment status updated',
      }));
      setDetailOpen(false);
      setSelectedAppointment(null);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update appointment status',
      }));
    }
  };

  const handleEditFromDetail = () => {
    if (selectedAppointment) {
      setEditingAppointment(selectedAppointment);
      setDetailOpen(false);
      setFormOpen(true);
    }
  };

  const getAppointmentStats = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayAppointments = appointments.filter(apt => apt.appointmentDate === today);
    
    return {
      total: appointments.length,
      today: todayAppointments.length,
      confirmed: appointments.filter(apt => apt.status === 'confirmed').length,
      completed: appointments.filter(apt => apt.status === 'completed').length,
    };
  };

  const stats = getAppointmentStats();

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Appointment Management
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Schedule and manage your salon appointments efficiently
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Appointments" arrow>
                <IconButton 
                  onClick={fetchAppointments}
                  disabled={fetching}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Schedule" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleAddAppointment(new Date())}
                sx={{
                  bgcolor: 'white',
                  color: '#2D3748',
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: '#1A202C',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
                    border: '2px solid white',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .MuiButton-startIcon': {
                    color: '#2D3748',
                  },
                }}
              >
                Book Appointment
              </Button>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Enhanced Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Total Appointments
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {stats.total}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Event sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        All bookings
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(240, 147, 251, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(240, 147, 251, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Today's Appointments
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {stats.today}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <AccessTime sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Today's schedule
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Schedule sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(79, 172, 254, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(79, 172, 254, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Confirmed
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {stats.confirmed}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Ready to serve
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 35 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <Card 
              sx={{ 
                height: '100%',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px rgba(250, 112, 154, 0.25)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(250, 112, 154, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontSize: '0.75rem',
                      }}
                    >
                      Completed
                    </Typography>
                    <Typography 
                      variant="h3" 
                      component="div" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 1,
                        fontSize: '2.5rem',
                      }}
                    >
                      {stats.completed}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CheckCircle sx={{ fontSize: 18, mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Successfully done
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      width: 70, 
                      height: 70,
                      color: 'white',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>✓</Typography>
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Tabs for Calendar and List View */}
      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Card 
          sx={{ 
            width: '100%',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'rgba(232, 180, 184, 0.2)' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  minHeight: 64,
                  py: 2,
                  color: '#718096',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#E8B4B8',
                    backgroundColor: 'rgba(232, 180, 184, 0.05)',
                  },
                  '&.Mui-selected': {
                    color: '#E8B4B8',
                    fontWeight: 700,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#E8B4B8',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab 
                icon={<CalendarToday sx={{ fontSize: 24 }} />} 
                label="Calendar View" 
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper': {
                    mr: 1,
                  },
                }}
              />
              <Tab 
                icon={<List sx={{ fontSize: 24 }} />} 
                label="List View" 
                iconPosition="start"
                sx={{
                  '& .MuiTab-iconWrapper': {
                    mr: 1,
                  },
                }}
              />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Fade in timeout={300}>
              <Box sx={{ p: 3 }}>
                <AppointmentCalendar
                  appointments={appointments}
                  onAppointmentClick={handleAppointmentClick}
                  onAddAppointment={handleAddAppointment}
                />
              </Box>
            </Fade>
          )}

          {tabValue === 1 && (
            <Fade in timeout={300}>
              <Box sx={{ p: 2 }}>
                <DataTable
                  columns={columns}
                  rows={appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalCount={appointments.length}
                  onPageChange={setPage}
                  onRowsPerPageChange={setRowsPerPage}
                  onView={handleAppointmentClick}
                  onEdit={handleEditAppointment}
                  loading={loading || fetching}
                />
              </Box>
            </Fade>
          )}
        </Card>
      </Fade>

      {/* Appointment Form Dialog */}
      <AppointmentForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingAppointment(null);
          setPreselectedDate(undefined);
          setPreselectedTime(undefined);
        }}
        onSubmit={handleFormSubmit}
        appointment={editingAppointment}
        preselectedDate={preselectedDate}
        preselectedTime={preselectedTime}
        loading={loading || fetching}
      />

      {/* Appointment Detail Dialog */}
      <AppointmentDetail
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onEdit={handleEditFromDetail}
        onConfirm={() => selectedAppointment && handleStatusChange(selectedAppointment.id, 'confirmed')}
        onStart={() => selectedAppointment && handleStatusChange(selectedAppointment.id, 'in_progress')}
        onComplete={() => selectedAppointment && handleStatusChange(selectedAppointment.id, 'completed')}
        onCancel={() => selectedAppointment && handleStatusChange(selectedAppointment.id, 'cancelled')}
      />
    </Box>
  );
};

export default Appointments;